import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy, Check, Inbox, MessageCircle, Send, Phone, Mail, Facebook, Globe, LifeBuoy, TicketPercent } from 'lucide-react';
import { toast } from 'sonner';
import { providerLabel, providerColor, egp } from '@/lib/format';

export const contactIconFor = (type) => ({
  whatsapp: MessageCircle,
  telegram: Send,
  phone: Phone,
  email: Mail,
  facebook: Facebook,
  website: Globe,
  other: LifeBuoy,
}[type] || LifeBuoy);

export const StatCard = ({ label, value, icon: Icon, hint, accent = true, testid }) => (
  <Card data-testid={testid} className={`relative overflow-hidden card-hover ${accent ? 'before:absolute before:inset-x-0 before:top-0 before:h-[3px] before:bg-primary/70' : ''}`}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="mt-2 text-2xl md:text-3xl font-semibold tabular-nums truncate">{value}</p>
          {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
        </div>
        {Icon && (
          <div className="shrink-0 rounded-xl bg-primary/10 text-primary p-2.5">
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

export const ProviderBadge = ({ provider }) => (
  <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${providerColor(provider)}`}>
    {providerLabel(provider)}
  </span>
);

/* ---- Discount badge + price tag (shared across Landing / Models) ---- */
export const DiscountBadge = ({ percent, testid }) => (
  <span
    className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/25"
    data-testid={testid}
  >
    <TicketPercent className="h-3 w-3" /> خصم {Math.round(percent)}%
  </span>
);

// Renders old (struck) + new price on a SINGLE line when discounted.
export const PriceTag = ({ price, original, discounted }) =>
  discounted ? (
    <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">
      <span className="text-[11px] text-muted-foreground line-through tabular-nums">{egp(original)}</span>
      <span className="font-semibold tabular-nums text-[hsl(var(--success))]">{egp(price)}</span>
    </span>
  ) : (
    <span className="font-semibold tabular-nums">{egp(price)}</span>
  );

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 min-w-0">
    <div className="min-w-0">
      <h1 className="text-xl sm:text-2xl font-semibold tracking-tight break-words">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1 break-words">{subtitle}</p>}
    </div>
    {children && <div className="flex items-center gap-2 shrink-0 flex-wrap">{children}</div>}
  </div>
);

export const EmptyState = ({ title, description, action, icon: Icon = Inbox }) => (
  <div className="border border-dashed rounded-xl bg-muted/30 p-10 text-center">
    <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-muted flex items-center justify-center">
      <Icon className="h-6 w-6 text-muted-foreground" />
    </div>
    <p className="font-medium">{title}</p>
    {description && <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const CopyButton = ({ text, className = '', label = '', testid }) => {
  const [copied, setCopied] = React.useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('تم النسخ');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('تعذّر النسخ');
    }
  };
  return (
    <Button type="button" variant="secondary" size="sm" onClick={onCopy} className={className}
            aria-label={label || (copied ? 'تم النسخ' : 'نسخ الكود')} title={label || 'نسخ الكود'} data-testid={testid}>
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {label && <span className="ms-1">{label}</span>}
    </Button>
  );
};

export const CodeBlock = ({ code, language = 'bash', title, testid }) => (
  <div className="rounded-xl border overflow-hidden max-w-full" style={{ background: '#0B1220', color: '#E6EDF7' }}>
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
      <span className="text-xs text-white/60 font-mono">{title || language}</span>
      <CopyButton text={code} testid={testid} />
    </div>
    <pre className="p-4 overflow-auto ltr text-xs md:text-sm max-w-full" data-testid={testid ? `${testid}-content` : undefined}>
      <code className="font-mono whitespace-pre">{code}</code>
    </pre>
  </div>
);

/* ---- Provider grouping + filter ---- */
export const PROVIDER_ORDER = { anthropic: 0, openai: 1, gemini: 2 };
export const PROVIDERS = [
  { id: 'all', label: 'الكل' },
  { id: 'openai', label: 'OpenAI' },
  { id: 'anthropic', label: 'Anthropic' },
  { id: 'gemini', label: 'Google' },
];

export const sortByProvider = (models) =>
  [...models].sort((a, b) => {
    const p = (PROVIDER_ORDER[a.provider] ?? 9) - (PROVIDER_ORDER[b.provider] ?? 9);
    if (p !== 0) return p;
    return (a.sort_order ?? 999) - (b.sort_order ?? 999);
  });

export const ProviderFilter = ({ value, onChange, testidPrefix = '' }) => (
  <div className="flex flex-wrap items-center gap-2">
    {PROVIDERS.map((p) => (
      <button
        key={p.id}
        type="button"
        onClick={() => onChange(p.id)}
        data-testid={`${testidPrefix}filter-${p.id}`}
        className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
          value === p.id
            ? 'bg-primary text-primary-foreground border-primary shadow-sm shadow-primary/20'
            : 'bg-card hover:bg-accent/60 text-muted-foreground'
        }`}
      >
        {p.label}
      </button>
    ))}
  </div>
);
